import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Home, Car, HeartPulse, Building2, Lock, ShieldOff } from "lucide-react";
import { matrixStrings as ms, INCOME_MID, SPLATKA_MID } from "../../lib/matrixStrings";
import { SmileLevel } from "./SmileLevel";

const PILLARS = ["pojisteni", "investice", "sporeni", "hypoteky", "uvery"];
const C = 200, R = 150;
const ANGLES = [-90, -18, 54, 126, 198];
const INS_ICONS = { majetek: Home, auto: Car, zivot: HeartPulse, firma: Building2 };
const RATE_KEYS = ["lt", "m1", "m2", "gt", "unknown"];
const RATE_LEVEL = { lt: 4, m1: 3, m2: 1, gt: 0, unknown: 2 };

const pt = (angle, value) => {
  const rad = (angle * Math.PI) / 180;
  const r = (value / 4) * R;
  return [C + r * Math.cos(rad), C + r * Math.sin(rad)];
};

const chipBase =
  "rounded-lg border px-3 py-2.5 text-xs font-semibold transition-all duration-200 flex items-center justify-center gap-1.5";
const chipOn = "border-teal-300 bg-teal-400/15 text-teal-200 shadow-[0_0_16px_rgba(45,212,191,0.15)]";
const chipOff = "border-teal-400/20 bg-white/5 text-slate-300 hover:border-teal-300/50";
const chipDisabled = "border-slate-700/60 bg-white/[0.02] text-slate-600 cursor-not-allowed";

const inputDark =
  "w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-2.5 font-mono text-sm text-white placeholder:text-slate-500 focus:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-400/20";

const lumpLevel = (raw) => {
  const n = Number(String(raw).replace(/[^0-9]/g, ""));
  if (!n) return null;
  return n < 100000 ? 2 : n < 500000 ? 3 : 4;
};

export const MatrixRadar = ({ lang, clientType = "osoba", onComplete }) => {
  const [ins, setIns] = useState([]);
  const [invests, setInvests] = useState(null);
  const [how, setHow] = useState(null);
  const [where, setWhere] = useState([]);
  const [amount, setAmount] = useState(null);
  const [lump, setLump] = useState("");
  const [sav, setSav] = useState([]);
  const [income, setIncome] = useState(null);
  const [incomeExact, setIncomeExact] = useState("");
  const [vydaje, setVydaje] = useState(null);
  const [vydajeExact, setVydajeExact] = useState("");
  const [hasHypo, setHasHypo] = useState(null);
  const [hypoRate, setHypoRate] = useState(null);
  const [hypoPay, setHypoPay] = useState("");
  const [hypoBal, setHypoBal] = useState("");
  const [hypoYears, setHypoYears] = useState("");
  const [hasUver, setHasUver] = useState(null);
  const [uverRate, setUverRate] = useState(null);
  const [uverPay, setUverPay] = useState("");
  const [uverBal, setUverBal] = useState("");
  const [uverYears, setUverYears] = useState("");
  const [vek, setVek] = useState(null);

  const num = (s) => Number(String(s).replace(/[^0-9]/g, "")) || null;

  const isBiz = clientType !== "osoba";

  const scores = useMemo(() => {
    const insTotal = isBiz ? 4 : 3;
    let inv = 2;
    if (invests === false) inv = 0;
    else if (invests === true) {
      if (how === "pravidelne" && amount !== null) inv = [1, 2, 3, 4][amount];
      else if (how === "jednorazove" && lumpLevel(lump) !== null) inv = lumpLevel(lump);
      else inv = 2;
    }
    return {
      pojisteni: ins.length
        ? ins.includes("nic")
          ? 0
          : Math.max(1, Math.round((ins.length / insTotal) * 4))
        : 2,
      investice: inv,
      sporeni: sav.length
        ? sav.includes("nic")
          ? 0
          : Math.min(4, sav.filter((s) => s !== "nic").length + 1)
        : 2,
      hypoteky: (() => {
        if (hasHypo === null) return 2;
        if (hasHypo === false) return 4;
        let lv = hypoRate ? RATE_LEVEL[hypoRate] : 2;
        const pay = num(hypoPay);
        const incM = Number(incomeExact.replace(/[^0-9]/g, "")) || (income !== null ? INCOME_MID[income] : null);
        if (pay !== null && incM && pay / incM > 0.35) lv = Math.max(0, lv - 1);
        return lv;
      })(),
      uvery: (() => {
        if (hasUver === null) return 2;
        if (hasUver === false) return 4;
        let lv = uverRate ? RATE_LEVEL[uverRate] : 2;
        const pay = num(uverPay);
        const incM = Number(incomeExact.replace(/[^0-9]/g, "")) || (income !== null ? INCOME_MID[income] : null);
        if (pay !== null && incM && pay / incM > 0.25) lv = Math.max(0, lv - 1);
        return lv;
      })(),
    };
  }, [ins, invests, how, amount, lump, sav, hasHypo, hypoRate, hypoPay, hasUver, uverRate, uverPay, income, incomeExact, isBiz]);

  const [display, setDisplay] = useState(scores);
  useEffect(() => {
    let raf;
    const tick = () => {
      setDisplay((d) => {
        const next = {};
        let done = true;
        for (const k of PILLARS) {
          const diff = scores[k] - d[k];
          next[k] = Math.abs(diff) < 0.02 ? scores[k] : d[k] + diff * 0.16;
          if (next[k] !== scores[k]) done = false;
        }
        if (!done) raf = requestAnimationFrame(tick);
        return next;
      });
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [scores]);

  const weakest = useMemo(() => PILLARS.reduce((a, b) => (scores[a] <= scores[b] ? a : b)), [scores]);

  const ready =
    ins.length > 0 &&
    invests !== null &&
    (invests === false || (how !== null && (how === "pravidelne" ? amount !== null : lumpLevel(lump) !== null))) &&
    sav.length > 0 &&
    (income !== null || Number(incomeExact.replace(/[^0-9]/g, "")) > 0) &&
    (vydaje !== null || Number(vydajeExact.replace(/[^0-9]/g, "")) > 0) &&
    hasHypo !== null &&
    (hasHypo === false || (hypoRate !== null && num(hypoPay) && num(hypoBal) && num(hypoYears))) &&
    hasUver !== null &&
    (hasUver === false || (uverRate !== null && num(uverPay) && num(uverBal) && num(uverYears))) &&
    vek !== null;

  const toggleIns = (key) =>
    setIns((s) => {
      if (key === "nic") return s.includes("nic") ? [] : ["nic"];
      const without = s.filter((x) => x !== "nic");
      return without.includes(key) ? without.filter((x) => x !== key) : [...without, key];
    });
  const toggleWhere = (key) => setWhere((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));
  const toggleSav = (key) =>
    setSav((s) => {
      if (key === "nic") return s.includes("nic") ? [] : ["nic"];
      const without = s.filter((x) => x !== "nic");
      return without.includes(key) ? without.filter((x) => x !== key) : [...without, key];
    });

  const points = PILLARS.map((p, i) => pt(ANGLES[i], display[p]).join(",")).join(" ");
  const rings = [0.25, 0.5, 0.75, 1];
  const d = ms.discovery;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="matrix-radar"
    >
      <p className="text-center font-mono text-[11px] font-semibold tracking-[0.3em] text-teal-400">{ms.radar.kicker[lang]}</p>
      <h2 className="font-heading mg-glow-text mt-4 text-center text-3xl font-extrabold text-white sm:text-4xl">{ms.radar.heading[lang]}</h2>
      <p className="mx-auto mt-4 max-w-xl text-center text-sm leading-relaxed text-slate-400">{ms.radar.sub[lang]}</p>

      <div className="mt-12 grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="mg-glass relative rounded-2xl p-6 lg:sticky lg:top-24">
          <svg viewBox="-45 0 490 400" className="w-full" data-testid="radar-chart">
            {rings.map((r) => (
              <polygon
                key={r}
                points={ANGLES.map((a) => pt(a, r * 4).join(",")).join(" ")}
                fill="none"
                stroke="rgba(45,212,191,0.18)"
                strokeWidth="1"
              />
            ))}
            {ANGLES.map((a, i) => {
              const [x, y] = pt(a, 4);
              const [lx, ly] = pt(a, 4.9);
              return (
                <g key={a}>
                  <line x1={C} y1={C} x2={x} y2={y} stroke="rgba(45,212,191,0.2)" strokeWidth="1" />
                  <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fill="#5EEAD4" fontSize="12" fontFamily="JetBrains Mono, monospace" fontWeight="700">
                    {ms.pillars[PILLARS[i]][lang].toUpperCase()}
                  </text>
                </g>
              );
            })}
            <polygon
              points={points}
              fill="rgba(45,212,191,0.14)"
              stroke="#2DD4BF"
              strokeWidth="2"
              style={{ filter: "drop-shadow(0 0 12px rgba(45,212,191,0.5))" }}
            />
            {PILLARS.map((p, i) => {
              const [x, y] = pt(ANGLES[i], display[p]);
              return (
                <g key={p}>
                  <circle cx={x} cy={y} r="10" fill="rgba(45,212,191,0.25)">
                    <animate attributeName="r" values="6;12;6" dur="2.2s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
                  </circle>
                  <circle cx={x} cy={y} r="4" fill="#2DD4BF" />
                </g>
              );
            })}
          </svg>
          <div className="mt-2 space-y-1.5">
            {PILLARS.map((p) => (
              <div key={p} className="flex items-center justify-between px-6" data-testid={`radar-value-${p}`}>
                <span className="font-mono text-[10px] tracking-widest text-slate-500">{ms.pillars[p][lang].toUpperCase()}</span>
                <span className="flex items-center gap-1.5">
                  <SmileLevel level={display[p]} size={15} testid={`radar-smile-${p}`} />
                  <span className="font-mono text-[10px] text-slate-400">{ms.levels[lang][Math.max(0, Math.min(4, Math.round(display[p])))]}</span>
                </span>
              </div>
            ))}
            <p className="pt-1 text-center font-mono text-[10px] tracking-widest text-teal-400/60">
              {ms.radar.focusPrefix[lang]}: {ms.pillars[weakest][lang].toUpperCase()}
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-prijem">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.prijem.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.prijem.hint[lang]}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {d.prijem.amounts[lang].map((label, i) => (
                <button type="button" key={i} onClick={() => setIncome(i)} data-testid={`income-${i}`} className={`${chipBase} font-mono ${income === i ? chipOn : chipOff}`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-400">{d.exact.q[lang]}</p>
            <input
              value={incomeExact}
              onChange={(e) => setIncomeExact(e.target.value)}
              placeholder={d.exact.ph[lang]}
              inputMode="numeric"
              className={`${inputDark} mt-2 max-w-[240px]`}
              data-testid="income-exact-input"
            />
            <p className="mt-1.5 text-[11px] text-teal-400/70" data-testid="income-exact-hint">{d.exact.hint[lang]}</p>
          </div>

          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-vydaje">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.vydaje.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.vydaje.hint[lang]}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {d.vydaje.amounts[lang].map((label, i) => (
                <button type="button" key={i} onClick={() => setVydaje(i)} data-testid={`vydaje-${i}`} className={`${chipBase} font-mono ${vydaje === i ? chipOn : chipOff}`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-400">{d.exact.q[lang]}</p>
            <input
              value={vydajeExact}
              onChange={(e) => setVydajeExact(e.target.value)}
              placeholder={d.exact.ph[lang]}
              inputMode="numeric"
              className={`${inputDark} mt-2 max-w-[240px]`}
              data-testid="vydaje-exact-input"
            />
            <p className="mt-1.5 text-[11px] text-teal-400/70" data-testid="vydaje-exact-hint">{d.exact.hint[lang]}</p>
          </div>

          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-vek">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.vek.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.vek.hint[lang]}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {d.vek.amounts[lang].map((label, i) => (
                <button type="button" key={i} onClick={() => setVek(i)} data-testid={`vek-${i}`} className={`${chipBase} font-mono ${vek === i ? chipOn : chipOff}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-pojisteni">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.pojisteni.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.pojisteni.hint[lang]}</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {["majetek", "auto", "zivot", "firma"].map((key) => {
                const Icon = INS_ICONS[key];
                const locked = key === "firma" && !isBiz;
                return (
                  <div key={key} className="relative">
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => toggleIns(key)}
                      data-testid={`ins-chip-${key}`}
                      className={`${chipBase} w-full ${locked ? chipDisabled : ins.includes(key) ? chipOn : chipOff}`}
                    >
                      {locked ? <Lock size={13} /> : <Icon size={14} />}
                      {d.pojisteni.options[key][lang]}
                    </button>
                    {locked && (
                      <p className="mt-1 text-center font-mono text-[9px] tracking-wide text-slate-600" data-testid="ins-firma-locked">
                        {d.pojisteni.firmaLocked[lang]}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => toggleIns("nic")}
              data-testid="ins-chip-nic"
              className={`${chipBase} mt-2 w-full ${ins.includes("nic") ? chipOn : chipOff}`}
            >
              <ShieldOff size={14} />
              {d.pojisteni.options.nic[lang]}
            </button>
          </div>

          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-investice">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.investice.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.investice.mainQ[lang]}</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setInvests(true)} data-testid="invests-yes" className={`${chipBase} ${invests === true ? chipOn : chipOff}`}>
                {d.investice.yes[lang]}
              </button>
              <button type="button" onClick={() => { setInvests(false); setHow(null); setAmount(null); setLump(""); setWhere([]); }} data-testid="invests-no" className={`${chipBase} ${invests === false ? chipOn : chipOff}`}>
                {d.investice.no[lang]}
              </button>
            </div>
            {invests === true && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                <p className="mt-4 text-xs font-semibold text-slate-300">{d.investice.howQ[lang]}</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {Object.entries(d.investice.how).map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => { setHow(key); setAmount(null); setLump(""); }}
                      data-testid={`invest-how-${key}`}
                      className={`${chipBase} ${how === key ? chipOn : chipOff}`}
                    >
                      {label[lang]}
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-400">{d.investice.whereQ[lang]}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(d.investice.where).map(([key, label]) => (
                    <button type="button" key={key} onClick={() => toggleWhere(key)} data-testid={`invest-where-${key}`} className={`${chipBase} ${where.includes(key) ? chipOn : chipOff}`}>
                      {label[lang]}
                    </button>
                  ))}
                </div>
                {how === "pravidelne" && (
                  <>
                    <p className="mt-4 text-xs text-slate-400">{d.investice.amountQ[lang]}</p>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {d.investice.amounts[lang].map((label, i) => (
                        <button type="button" key={i} onClick={() => setAmount(i)} data-testid={`invest-amount-${i}`} className={`${chipBase} font-mono ${amount === i ? chipOn : chipOff}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {how === "jednorazove" && (
                  <>
                    <p className="mt-4 text-xs text-slate-400">{d.investice.lumpQ[lang]}</p>
                    <input
                      value={lump}
                      onChange={(e) => setLump(e.target.value)}
                      placeholder={d.investice.lumpPh[lang]}
                      inputMode="numeric"
                      className={`${inputDark} mt-2 max-w-[240px]`}
                      data-testid="invest-lump-input"
                    />
                  </>
                )}
              </motion.div>
            )}
          </div>

          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-sporeni">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.sporeni.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.sporeni.hint[lang]}</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {Object.entries(d.sporeni.options).map(([key, label]) => (
                <button type="button" key={key} onClick={() => toggleSav(key)} data-testid={`sav-chip-${key}`} className={`${chipBase} ${sav.includes(key) ? chipOn : chipOff}`}>
                  {label[lang]}
                </button>
              ))}
            </div>
          </div>

          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-hypoteky">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.hypoteky.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.hypoteky.hint[lang]}</p>
            <p className="mt-3 text-xs font-semibold text-slate-300">{d.hypoteky.mainQ[lang]}</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setHasHypo(true)} data-testid="hypo-yes" className={`${chipBase} ${hasHypo === true ? chipOn : chipOff}`}>
                {d.hypoteky.yes[lang]}
              </button>
              <button type="button" onClick={() => { setHasHypo(false); setHypoRate(null); setHypoPay(""); setHypoBal(""); setHypoYears(""); }} data-testid="hypo-no" className={`${chipBase} ${hasHypo === false ? chipOn : chipOff}`}>
                {d.hypoteky.no[lang]}
              </button>
            </div>
            {hasHypo === true && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                <p className="mt-4 text-xs text-slate-400">{d.hypoteky.rateQ[lang]}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {d.hypoteky.rates[lang].map((label, i) => (
                    <button type="button" key={RATE_KEYS[i]} onClick={() => setHypoRate(RATE_KEYS[i])} data-testid={`hypo-rate-${RATE_KEYS[i]}`} className={`${chipBase} font-mono ${hypoRate === RATE_KEYS[i] ? chipOn : chipOff}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-400">{d.hypoteky.payQ[lang]}</p>
                <input value={hypoPay} onChange={(e) => setHypoPay(e.target.value)} placeholder={d.exact.ph[lang]} inputMode="numeric" className={`${inputDark} mt-2 max-w-[240px]`} data-testid="hypo-pay-input" />
                <p className="mt-4 text-xs text-slate-400">{d.hypoteky.borrowQ[lang]}</p>
                <input value={hypoBal} onChange={(e) => setHypoBal(e.target.value)} placeholder={lang === "cs" ? "např. 2 400 000" : "e.g. 2,400,000"} inputMode="numeric" className={`${inputDark} mt-2 max-w-[240px]`} data-testid="hypo-balance-input" />
                <p className="mt-4 text-xs text-slate-400">{d.hypoteky.yearsQ[lang]}</p>
                <input value={hypoYears} onChange={(e) => setHypoYears(e.target.value)} placeholder={lang === "cs" ? "např. 22" : "e.g. 22"} inputMode="numeric" className={`${inputDark} mt-2 max-w-[240px]`} data-testid="hypo-years-input" />
                <p className="mt-1.5 text-[11px] text-teal-400/70">{d.exact.hint[lang]}</p>
              </motion.div>
            )}
          </div>

          <div className="mg-glass rounded-2xl p-6" data-testid="discovery-uvery">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-teal-300">{d.uvery.label[lang]}</p>
            <p className="mt-1 text-xs text-slate-400">{d.uvery.hint[lang]}</p>
            <p className="mt-3 text-xs font-semibold text-slate-300">{d.uvery.mainQ[lang]}</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setHasUver(true)} data-testid="uver-yes" className={`${chipBase} ${hasUver === true ? chipOn : chipOff}`}>
                {d.uvery.yes[lang]}
              </button>
              <button type="button" onClick={() => { setHasUver(false); setUverRate(null); setUverPay(""); setUverBal(""); setUverYears(""); }} data-testid="uver-no" className={`${chipBase} ${hasUver === false ? chipOn : chipOff}`}>
                {d.uvery.no[lang]}
              </button>
            </div>
            {hasUver === true && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                <p className="mt-4 text-xs text-slate-400">{d.uvery.rateQ[lang]}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {d.uvery.rates[lang].map((label, i) => (
                    <button type="button" key={RATE_KEYS[i]} onClick={() => setUverRate(RATE_KEYS[i])} data-testid={`uver-rate-${RATE_KEYS[i]}`} className={`${chipBase} font-mono ${uverRate === RATE_KEYS[i] ? chipOn : chipOff}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-400">{d.uvery.payQ[lang]}</p>
                <input value={uverPay} onChange={(e) => setUverPay(e.target.value)} placeholder={d.exact.ph[lang]} inputMode="numeric" className={`${inputDark} mt-2 max-w-[240px]`} data-testid="uver-pay-input" />
                <p className="mt-4 text-xs text-slate-400">{d.uvery.borrowQ[lang]}</p>
                <input value={uverBal} onChange={(e) => setUverBal(e.target.value)} placeholder={lang === "cs" ? "např. 180 000" : "e.g. 180,000"} inputMode="numeric" className={`${inputDark} mt-2 max-w-[240px]`} data-testid="uver-balance-input" />
                <p className="mt-4 text-xs text-slate-400">{d.uvery.yearsQ[lang]}</p>
                <input value={uverYears} onChange={(e) => setUverYears(e.target.value)} placeholder={lang === "cs" ? "např. 5" : "e.g. 5"} inputMode="numeric" className={`${inputDark} mt-2 max-w-[240px]`} data-testid="uver-years-input" />
                <p className="mt-1.5 text-[11px] text-teal-400/70">{d.exact.hint[lang]}</p>
              </motion.div>
            )}
          </div>

          <button
            onClick={() =>
              ready &&
              onComplete({
                scores,
                income,
                vydaje,
                vek,
                incomeExact: Number(incomeExact.replace(/[^0-9]/g, "")) || null,
                vydajeExact: Number(vydajeExact.replace(/[^0-9]/g, "")) || null,
                rates: { hypo: hasHypo ? hypoRate : null, uver: hasUver ? uverRate : null },
                hasHypo,
                hasUver,
                loanDetails: {
                  hypo: hasHypo ? { rate: hypoRate, pay: num(hypoPay), bal: num(hypoBal), years: num(hypoYears) } : null,
                  uver: hasUver ? { rate: uverRate, pay: num(uverPay), bal: num(uverBal), years: num(uverYears) } : null,
                },
                answers: { ins, invests, how, amount, lump, sav, where },
              })
            }
            disabled={!ready}
            data-testid="run-synthesis-button"
            className="w-full rounded-full bg-teal-500 py-4 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_30px_rgba(45,212,191,0.4)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {ms.radar.continueBtn[lang]}
          </button>
          {!ready && <p className="text-center font-mono text-[10px] tracking-widest text-slate-500">{ms.radar.continueHint[lang]}</p>}
        </div>
      </div>
    </motion.div>
  );
};
